import { useState } from 'react'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import type { Entity } from '@models/Entity'

import type { Project } from '../../../aansluiting/project/types/Project'
import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { SelectString } from '../../../core/components/SelectString'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import { type Subproces, SubprocesTypes } from '../types/Subproces'
import type { SubprocesDialogType } from '../types/SubprocesDialogType'
import type { SubprocesTemplate } from '../types/SubprocesTemplate'

import { Cancel } from './dialogs/Cancel'
import { CancelSchouw } from './dialogs/CancelSchouw'
import { CreateBodem } from './dialogs/CreateBodem'
import { CreateNetcheck } from './dialogs/CreateNetcheck'
import { CreateSchouw } from './dialogs/CreateSchouw'
import { HerplanNetcheck } from './dialogs/HerplanNetcheck'
import { HerplanSchouw } from './dialogs/HerplanSchouw'
import { PauseSubproces } from './dialogs/PauseSubproces'
import { PlanSchouw } from './dialogs/PlanSchouw'
import { ResumeBodem } from './dialogs/ResumeBodem'
import { ResumeNetcheck } from './dialogs/ResumeNetcheck'
import { ReviewSchouw } from './dialogs/ReviewSchouw'

const TYPE_LABEL_MAP: Record<SubprocesDialogType, string> = {
  create: 'Aanmaken',
  edit: 'Plan',
  review: 'Beoordeel',
  cancel: 'Annuleer',
  herplan: 'Herplan',
  pause: 'Pauzeer',
  commit: 'Ter beoordeling aanbieden',
  complete: 'Opleveren',
  resume: 'Hervatten',
} as const

interface Props {
  type?: SubprocesDialogType
  subproces?: Subproces
  entity: Entity
  user: AuthenticatedUser
  aannemers?: string[]
  templates: SubprocesTemplate[]
  project: Project | HoofdleidingProject
  onClose: () => void
}

export const SubprocesDialog = ({
  type,
  subproces,
  entity,
  aannemers,
  templates,
  user,
  project,
  onClose,
}: Props) => {
  const [typeOfSubproces, setTypeOfSubproces] = useState<
    keyof typeof SubprocesTypes | ''
  >((templates.at(0)?.type as keyof typeof SubprocesTypes) || '')

  if (!type) return null

  return (
    <Dialog open fullWidth maxWidth={'md'}>
      <DialogTitle>
        {`${TYPE_LABEL_MAP[type]} subproces ${
          type !== 'create' ? subproces?.type : ''
        }  voor ${entity.type} ${entity.id}`}
      </DialogTitle>

      {type === 'create' && !!entity && (
        <>
          <DialogContent>
            <SelectString
              multiple={false}
              disableClearable={false}
              options={templates.map((t) => t.type)}
              fullWidth
              getOptionLabel={(option) => SubprocesTypes[option] || ''}
              label={
                templates.length === 0
                  ? 'Geen subprocessen gevonden'
                  : 'Type subproces'
              }
              value={typeOfSubproces}
              variant="standard"
              onChange={(value) =>
                setTypeOfSubproces(value as keyof typeof SubprocesTypes)
              }
            />
          </DialogContent>
          {typeOfSubproces === 'schouw' && user && (
            <CreateSchouw
              user={user}
              aannemers={aannemers || []}
              entity={entity}
              onClose={onClose}
            />
          )}

          {typeOfSubproces === 'bodem' && user && (
            <CreateBodem
              project={project as HoofdleidingProject}
              entity={entity}
              onClose={onClose}
            />
          )}

          {typeOfSubproces === 'netcheck' && user && (
            <CreateNetcheck
              project={project as HoofdleidingProject}
              entity={entity}
              onClose={onClose}
            />
          )}

          {!typeOfSubproces && (
            <DialogActions>
              <Button color="primary" onClick={onClose} autoFocus>
                Annuleren
              </Button>
            </DialogActions>
          )}
        </>
      )}

      {subproces && entity && (
        <>
          {type === 'pause' && (
            <PauseSubproces subproces={subproces} onClose={onClose} />
          )}
        </>
      )}

      {subproces && entity && subproces.type === 'schouw' && (
        <>
          {type === 'edit' && (
            <PlanSchouw subproces={subproces} onClose={onClose} />
          )}
          {type === 'review' && (
            <ReviewSchouw subproces={subproces} onClose={onClose} />
          )}
          {type === 'cancel' && (
            <CancelSchouw subproces={subproces} onClose={onClose} />
          )}
          {type === 'herplan' && (
            <HerplanSchouw subproces={subproces} onClose={onClose} />
          )}
        </>
      )}

      {subproces && entity && subproces.type === 'bodem' && (
        <>
          {type === 'resume' && (
            <ResumeBodem subproces={subproces} onClose={onClose} />
          )}
          {type === 'cancel' && (
            <Cancel subproces={subproces} onClose={onClose} />
          )}
        </>
      )}

      {subproces && entity && subproces.type === 'netcheck' && (
        <>
          {type === 'resume' && (
            <ResumeNetcheck subproces={subproces} onClose={onClose} />
          )}
          {type === 'cancel' && (
            <Cancel subproces={subproces} onClose={onClose} />
          )}
          {type === 'herplan' && (
            <HerplanNetcheck subproces={subproces} onClose={onClose} />
          )}
        </>
      )}
    </Dialog>
  )
}
